import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { apiClient } from '../network/ApiClient';
import { AppSoundManager } from '../audio/AppSoundManager';
import { PageContent } from '../components/ui/PageContent';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { GradientText } from '../components/ui/GradientText';
import { Footer } from '../components/shell/Footer';
import { ASSETS } from '../config/assets';
import { PlayerAvatar } from '../components/ui/PlayerAvatar';
import { CountryFlag } from '../components/ui/CountryFlag';
import type { SessionListItem, SessionPlayer } from '../types/game';
import { useTranslation } from '../i18n/react';
import { renderUiMessage, uiFromError, uiKey, type UiMessage } from '../i18n/uiMessage';
import { getPlayerName } from '../utils/playerName';

/**
 * PLAY ONLINE / LOBBY — open-table browser (Figma "PLAY ONLINE / LOBBY" frame,
 * 2560×3774 export, values ×0.75, DS2 fluid tokens for the shared eyebrow /
 * heading / paragraph styles). Built on the responsive DS2 architecture, NOT
 * the absolute 2560 canvas.
 *
 * Fully wired to the live session APIs (same endpoints the old SelectTableScreen
 * used) — no placeholder data:
 *   • Table grid ← apiClient.getSessions(), polled every 5s.
 *   • Find me a table → resume my active session, else join the first open
 *     waiting table, else create one.
 *   • Create a table → resume a waiting session I already own, else create one
 *     → /lobby/:id (the current create-table screen until it is redesigned).
 *   • Clicking an empty seat joins that exact seat; clicking a card body joins
 *     the table's first open seat; clicking a table that is already mine just
 *     navigates to its lobby (no re-join call).
 *
 * Player Identity V1: names and avatars are authoritative from the payload —
 * display_name is the backend's canonical name (rendered verbatim, no
 * client-side capitalisation) and avatar_key is the human preset or the
 * bot's stable per-session assignment (no key → initial fallback).
 */

// ── Session → lobby-card model ───────────────────────────────────────────────

type Team = 'A' | 'B'; // A = red seats (top/bottom), B = blue seats (left/right)

interface Seat {
  position: number;
  taken:    boolean;
  name?:    string;
  /** Raw avatar key from the payload — PlayerAvatar validates it. */
  avatarKey?: string | null;
  isBot?:   boolean;
  /** Stable bot difficulty from the payload; the render site localizes the
   *  bot label from it via the shared getPlayerName seam. */
  botDifficulty?: string | null;
  /** Raw country code from the payload — CountryFlag validates it. */
  countryCode?: string | null;
}

interface LobbyTable {
  sessionId: number;
  /** Host display name (verbatim user data); null when the table has no players yet. */
  host:      string | null;
  /** Raw country code from the payload — CountryFlag validates it. */
  hostCountryCode?: string | null;
  /** The host seat is reassignable and a bot can occupy any seat position,
   *  so the flag must still be gated — never assume "host" implies human. */
  hostIsBot: boolean;
  /** Stable bot difficulty when the host seat is a bot; null otherwise. */
  hostBotDifficulty?: string | null;
  isMine:    boolean;
  status:    'open' | 'in_progress';
  seats:     { top: Seat; right: Seat; bottom: Seat; left: Seat };
}

/** Figma/legacy seat layout: 0=bottom(A/red), 1=right(B/blue), 2=top(A/red), 3=left(B/blue). */
function seatFor(players: SessionPlayer[], position: number): Seat {
  const player = players.find(p => p.position === position);
  if (!player) return { position, taken: false };
  return {
    position,
    taken:  true,
    // Canonical display name rendered verbatim — no client-side casing, so
    // the same player reads identically here, in the lobby, and in game.
    name:        player.display_name,
    avatarKey:   player.avatar_key ?? null,
    isBot:       player.is_bot,
    botDifficulty: player.bot_difficulty ?? null,
    countryCode: player.country_code ?? null,
  };
}

function deriveTable(session: SessionListItem): LobbyTable {
  // The host can move seats after creating the table (MoveSeatService) and a
  // bot can be seated at any position — so the table's host identity must
  // come from the authoritative is_host flag, never from seat position 0.
  const host = session.players.find(p => p.is_host) ?? session.players[0];
  return {
    sessionId: session.session_id,
    host:      host ? host.display_name : null,
    hostCountryCode: host?.country_code ?? null,
    hostIsBot: host?.is_bot ?? false,
    hostBotDifficulty: host?.bot_difficulty ?? null,
    isMine:    session.is_my_session,
    status:    session.status === 'active' ? 'in_progress' : 'open',
    seats: {
      bottom: seatFor(session.players, 0),
      right:  seatFor(session.players, 1),
      top:    seatFor(session.players, 2),
      left:   seatFor(session.players, 3),
    },
  };
}

const openSeatCount = (t: LobbyTable) =>
  Object.values(t.seats).filter(s => !s.taken).length;

/** Card title: "{host}'s Table" / "Mesa de {host}" — a whole sentence per
 *  locale (no possessive concatenation); host-less tables get a numbered
 *  fallback. Shared by the card and the search filter so both agree. */
function useTableTitle() {
  const { t } = useTranslation('play');
  return (table: LobbyTable) =>
    table.host !== null
      ? t('online.tableTitle', {
          host: getPlayerName(
            { display_name: table.host, is_bot: table.hostIsBot, bot_difficulty: table.hostBotDifficulty },
            table.host,
          ),
        })
      : t('online.tableFallback', { id: table.sessionId });
}

// ── Small shared bits ────────────────────────────────────────────────────────

const EYEBROW: CSSProperties = {
  fontSize:      `clamp(1rem, ${DS2.type.eyebrow.pref}vw, ${DS2.type.eyebrow.max}px)`,
  fontWeight:    DS2.type.eyebrow.weight,
  fontFamily:    DS2.font.body,
  color:         DS2.color.gold,
  letterSpacing: '0.158em',
  textTransform: 'uppercase',
};

const SECTION_HEADING: CSSProperties = {
  fontSize:      `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
  lineHeight:    DS2.type.sectionHeading.lineHeight,
  letterSpacing: '-0.02em',
  fontWeight:    700,
  fontFamily:    DS2.font.heading,
  color:         DT.color.textPrimary,
  margin:        0,
};

const PARAGRAPH: CSSProperties = {
  fontSize:   `clamp(0.9rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
  lineHeight: DS2.type.paragraph.lineHeight,
  fontFamily: DS2.font.body,
  fontWeight: 300,
  color:      DS2.color.textBright,
  margin:     0,
};

/** Blurred status dot (Figma Ellipse: 20px, blur 2). */
function StatusDot({ color, size = 13 }: { color: string; size?: number }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: '50%', background: color, filter: 'blur(1.5px)', flexShrink: 0 }} />;
}

// ── Seat chip (red team A top/bottom, blue team B left/right) ────────────────

const TEAM_BG: Record<Team, string> = { A: '#880000', B: '#2371C0' };

function SeatChip({ seat, team, orientation, onClaim, compact }: {
  seat: Seat; team: Team; orientation: 'portrait' | 'landscape'; onClaim?: (position: number) => void; compact?: boolean;
}) {
  const { t } = useTranslation('play');
  const w = orientation === 'portrait' ? (compact ? 34 : 40) : (compact ? 48 : 58);
  const h = orientation === 'portrait' ? (compact ? 48 : 58) : (compact ? 34 : 40);
  const avatarSize = compact ? 24 : 29;
  const base: CSSProperties = {
    width: w, height: h, borderRadius: compact ? 10 : 12, boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: compact ? 2 : 3,
  };

  if (!seat.taken) {
    // Open seat — colored rect, dashed gold border, gold "+". Clicking claims
    // THIS exact seat (stopPropagation so the card's own onClick doesn't also fire).
    return (
      <button
        type="button"
        aria-label={t('online.joinSeat')}
        onClick={e => { e.stopPropagation(); onClaim?.(seat.position); }}
        style={{ ...base, background: TEAM_BG[team], border: '1px dashed #C9A84C', cursor: onClaim ? 'pointer' : 'default', padding: 0, WebkitTapHighlightColor: 'transparent' }}
      >
        <span style={{ fontFamily: DS2.font.body, fontWeight: 500, fontSize: compact ? 16 : 19, lineHeight: 1, color: '#FFD255' }}>+</span>
      </button>
    );
  }

  // Taken seat — colored rect with avatar + name. Human seats carry a small
  // country-flag badge on the avatar's corner (real flag, or the neutral
  // globe when unset/invalid); bots get no badge, matching the lobby rule.
  // Localized seat label via the shared structured resolver (bots by stable
  // difficulty; humans verbatim; unknown difficulty → verbatim display name).
  const seatLabel = getPlayerName(
    { display_name: seat.name, is_bot: seat.isBot, bot_difficulty: seat.botDifficulty },
    seat.name ?? '',
  );
  return (
    <div style={{ ...base, background: TEAM_BG[team] }}>
      <span style={{ position: 'relative', width: avatarSize, height: avatarSize, flexShrink: 0 }}>
        {/* Authoritative avatar: preset / stable bot assignment / initial. */}
        <PlayerAvatar
          avatarKey={seat.avatarKey}
          name={seatLabel}
          size={avatarSize}
          loading="eager"
          style={{ border: '1px solid #FFFFFF', boxSizing: 'border-box' }}
          initialStyle={{ background: '#F5F5F5', color: '#0b0b0b', fontFamily: DS2.font.heading, fontSize: compact ? 10 : 11.3 }}
        />
        {!seat.isBot && (
          <CountryFlag
            countryCode={seat.countryCode}
            width={compact ? 12 : 13.3}
            height={compact ? 9 : 10}
            loading="eager"
            style={{ position: 'absolute', right: -5, bottom: -3, boxShadow: '0 0 0 1px rgba(0,0,0,0.4)' }}
          />
        )}
      </span>
      <span title={seatLabel} style={{ fontFamily: DS2.font.heading, fontWeight: 700, fontSize: compact ? 9 : 9.5, lineHeight: 1, color: '#FFFFFF', maxWidth: w - 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {seatLabel}
      </span>
    </div>
  );
}

/** Table thumbnail with four seats around it (contained widget — absolute
 *  positioning is relative to THIS box, not the page). */
function TableWidget({ table, onClaim, compact }: { table: LobbyTable; onClaim?: (position: number) => void; compact?: boolean }) {
  const TABLE = compact ? 130 : 170;   // table image side
  const PAD   = compact ? 26  : 32;    // seat protrusion on each edge
  const box   = TABLE + PAD * 2;
  const seatWrap = (extra: CSSProperties): CSSProperties => ({ position: 'absolute', ...extra });
  return (
    <div style={{ position: 'relative', width: box, height: box, flexShrink: 0 }}>
      <img
        src={ASSETS.board.TABLE_LOBBY}
        alt=""
        style={{ position: 'absolute', left: PAD, top: PAD, width: TABLE, height: TABLE, objectFit: 'contain', borderRadius: compact ? 16 : 20 }}
        draggable={false}
      />
      <div style={seatWrap({ top: 0, left: '50%', transform: 'translateX(-50%)' })}>
        <SeatChip seat={table.seats.top} team="A" orientation="portrait" onClaim={onClaim} compact={compact} />
      </div>
      <div style={seatWrap({ bottom: 0, left: '50%', transform: 'translateX(-50%)' })}>
        <SeatChip seat={table.seats.bottom} team="A" orientation="portrait" onClaim={onClaim} compact={compact} />
      </div>
      <div style={seatWrap({ left: 0, top: '50%', transform: 'translateY(-50%)' })}>
        <SeatChip seat={table.seats.left} team="B" orientation="landscape" onClaim={onClaim} compact={compact} />
      </div>
      <div style={seatWrap({ right: 0, top: '50%', transform: 'translateY(-50%)' })}>
        <SeatChip seat={table.seats.right} team="B" orientation="landscape" onClaim={onClaim} compact={compact} />
      </div>
    </div>
  );
}

// ── Table card ───────────────────────────────────────────────────────────────

function TableCard({ table, onJoin, compact }: { table: LobbyTable; onJoin: (sessionId: number, isMine: boolean, position?: number) => void; compact?: boolean }) {
  const { t } = useTranslation('play');
  const tableTitle = useTableTitle();
  const inProgress = table.status === 'in_progress';
  const open = openSeatCount(table);
  // Clickable everywhere: mine → straight to lobby; open seats → first open one.
  const clickable = table.isMine || open > 0;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onJoin(table.sessionId, table.isMine, table.seats.bottom.taken ? undefined : 0) : undefined}
      style={{
        boxSizing:    'border-box',
        display:      'flex',
        flexWrap:     'wrap',
        // flex-start (not center) — the title anchors to the card's upper-left
        // corner, matching Figma; it does not vertically center against the
        // much-taller table widget.
        alignItems:   'flex-start',
        gap:          compact ? 12 : 16,
        padding:      compact ? 16 : 21,
        borderRadius: compact ? 13 : 14.3,
        border:       `1px solid ${DS2.color.gold}`,
        background:   inProgress ? 'rgba(255,0,0,0.08)' : '#131313',
        cursor:       clickable ? 'pointer' : 'default',
      }}
    >
      <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8.7 }}>
        {/* Title — "<host>'s Table", upper-left, compact. No overflowWrap:anywhere
            — that broke mid-word ("CARLOSD'" / "S TABLE"); default wrapping only
            breaks at the space before "Table". Host's flag precedes the title
            — gated on hostIsBot, since a bot can occupy the host's former seat
            after the host relocates (bots never show a flag). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!table.hostIsBot && <CountryFlag countryCode={table.hostCountryCode} width={compact ? 13 : 14} />}
          <div style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: compact ? 13 : 'clamp(0.84rem, 0.95vw, 15.3px)', lineHeight: 1.3, letterSpacing: '0.05em', textTransform: 'uppercase', color: DS2.color.gold, wordBreak: 'normal' }}>
            {tableTitle(table)}
          </div>
        </div>
        {table.isMine && (
          <div style={{ fontSize: compact ? 9.5 : 10, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: DS2.color.textBright, opacity: 0.6 }}>
            {t('online.yourTable')}
          </div>
        )}

        {/* Status — open seats OR in-progress badge */}
        {inProgress ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start', padding: compact ? '4px 9px' : '5px 10px', borderRadius: 7, background: 'rgba(255,1,1,0.09)', border: '1px solid #FF0101' }}>
            <StatusDot color="#FF0000" size={compact ? 7 : 7.7} />
            <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: compact ? 10 : 10.7, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF0101' }}>{t('online.inProgress')}</span>
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <StatusDot color="#00FF09" size={compact ? 7 : 7.7} />
            <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: compact ? 15 : 16.7, color: DS2.color.gold, letterSpacing: '0.03em' }}>{open}</span>
            {/* Plural-rule label; the number is the styled span before it. */}
            <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: compact ? 11 : 12, color: DS2.color.textBright, letterSpacing: '0.04em' }}>{t('online.openSeats', { count: open })}</span>
          </span>
        )}
      </div>

      <TableWidget table={table} onClaim={pos => onJoin(table.sessionId, table.isMine, pos)} compact={compact} />
    </div>
  );
}

function CreateTableCard({ onClick, busy, compact }: { onClick: () => void; busy: boolean; compact?: boolean }) {
  const { t } = useTranslation('play');
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          boxSizing:   'border-box',
          display:     'flex',
          alignItems:  'center',
          gap:         14,
          padding:     '14px 16px',
          borderRadius: 13,
          border:      '1px dashed #C9A84C',
          background:  '#1D1D1D',
          cursor:      busy ? 'default' : 'pointer',
          opacity:     busy ? 0.6 : 1,
          textAlign:   'left',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span style={{ width: 38, height: 38, flexShrink: 0, borderRadius: '50%', border: '1px dashed #C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 500, color: '#FFD255', lineHeight: 1 }}>+</span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: 15, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#FFFFFF' }}>{t('online.createTable')}</span>
          <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: 12, letterSpacing: '0.02em', color: DS2.color.textBright, opacity: 0.5 }}>
            {t('online.createTableSubCompact')}
          </span>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        boxSizing:    'border-box',
        display:      'flex',
        flexDirection:'column',
        alignItems:   'center',
        justifyContent:'center',
        gap:          12,
        minHeight:    227,
        padding:      21,
        borderRadius: 14.3,
        border:       '1px dashed #C9A84C',
        background:   '#1D1D1D',
        cursor:       busy ? 'default' : 'pointer',
        opacity:      busy ? 0.6 : 1,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ width: 57, height: 57, borderRadius: '50%', border: '1px dashed #C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, fontWeight: 500, color: '#FFD255', lineHeight: 1 }}>+</span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: 'clamp(1.05rem, 1.3vw, 22px)', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF' }}>{t('online.createTable')}</span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: 16, letterSpacing: '0.04em', textAlign: 'center', color: DS2.color.textBright, opacity: 0.5, maxWidth: 307 }}>
        {t('online.createTableSub')}
      </span>
    </button>
  );
}

// ── Gold action button (banner CTA) — DS2 gold treatment, action (not a Link) ─
function GoldActionButton({ children, onClick, busy, compact }: { children: ReactNode; onClick: () => void; busy: boolean; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          compact ? '100%' : undefined,
        padding:        compact ? '12px 20px' : '13px 23px',
        background:     DS2.gradient.gold,
        // Figma-exact: color: #0B0B0B (dark text on gold for THIS button —
        // distinct from other DS2 gold CTAs which spec light text).
        color:          '#0B0B0B',
        border:         '0.75px solid #C9A84C',
        borderRadius:   compact ? 10 : 10.7,
        fontSize:       compact ? 15 : 16.7,
        fontWeight:     500,
        fontFamily:     DS2.font.body,
        letterSpacing:  '0.033em',
        textTransform:  'uppercase',
        boxShadow:      DT.shadow.gold,
        cursor:         busy ? 'default' : 'pointer',
        opacity:        busy ? 0.6 : 1,
        whiteSpace:     'nowrap',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${DT.color.border}`, borderTopColor: DS2.color.gold, animation: 'spin 0.75s linear infinite' }} />
    </div>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function PlayOnlineScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t } = useTranslation('play');
  const tableTitle = useTableTitle();
  const [busy, setBusy] = useState(false);
  // Frontend-owned errors are stored as keys (re-render on language switch);
  // backend prose stays raw — see i18n/uiMessage.ts.
  const [error, setError] = useState<UiMessage | null>(null);
  const [query, setQuery] = useState('');

  // Concise message forwarded from another screen (e.g. the lobby, when the
  // host removed this user). Shown once, then cleared from history state so a
  // refresh/back doesn't resurface it. This is backend prose today (the
  // participant_removed broadcast message) — displayed verbatim.
  useEffect(() => {
    const notice = (location.state as { notice?: string } | null)?.notice;
    if (notice) {
      setError({ kind: 'raw', text: notice });
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Live session list — same endpoint/poll cadence as the old SelectTableScreen ──
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchSessions() {
      try {
        const data = await apiClient.getSessions();
        if (cancelled) return;
        setSessions(data.sessions.filter(s => s.status === 'waiting' || s.status === 'active'));
      } catch {
        if (!cancelled) setError(uiKey('online.errorLoad'));
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    }
    fetchSessions();
    const id = setInterval(fetchSessions, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Create a table — the existing flow (resume waiting session, else create).
  async function handleCreate() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const data = await apiClient.getSessions();
      if (data.my_active_session_id) {
        const mine = data.sessions.find((s: SessionListItem) => s.session_id === data.my_active_session_id);
        if (mine?.status === 'waiting') { navigate(`/lobby/${data.my_active_session_id}`); return; }
        setError(uiKey('online.errorAlreadyActive'));
        return;
      }
      const res = await apiClient.createSession();
      AppSoundManager.play('accept');
      navigate(`/lobby/${res.session_id}`);
    } catch (err) {
      AppSoundManager.play('deny');
      setError(uiFromError(err, 'online.errorCreate'));
    } finally {
      setBusy(false);
    }
  }

  // Find me a table — resume active, else join the first open waiting table,
  // else create one.
  async function handleFindTable() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const data = await apiClient.getSessions();
      if (data.my_active_session_id) { navigate(`/lobby/${data.my_active_session_id}`); return; }
      const open = data.sessions.find(
        (s: SessionListItem) => s.status === 'waiting' && s.player_count < s.max_players,
      );
      if (open) {
        const taken = new Set(open.players.map(p => p.position));
        const pos = [0, 1, 2, 3].find(p => !taken.has(p));
        const res = await apiClient.joinSession(open.session_id, undefined, pos);
        AppSoundManager.play('accept');
        navigate(`/lobby/${res.session_id}`);
        return;
      }
      const res = await apiClient.createSession();
      AppSoundManager.play('accept');
      navigate(`/lobby/${res.session_id}`);
    } catch (err) {
      AppSoundManager.play('deny');
      setError(uiFromError(err, 'online.errorFind'));
    } finally {
      setBusy(false);
    }
  }

  // Join a specific table — from a card click (first open seat) or a specific
  // seat chip. Already-mine tables just navigate (no re-join call).
  async function handleJoinTable(sessionId: number, isMine: boolean, position?: number) {
    if (isMine) { navigate(`/lobby/${sessionId}`); return; }
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const res = await apiClient.joinSession(sessionId, undefined, position);
      AppSoundManager.play('accept');
      navigate(`/lobby/${res.session_id}`);
    } catch {
      AppSoundManager.play('deny');
      setError(uiKey('online.errorJoin'));
      setBusy(false);
    }
  }

  const tables = sessions.map(deriveTable);
  const openTables = tables.filter(tb => tb.status === 'open').length;
  const playingNow = sessions.reduce((n, s) => n + s.player_count, 0);

  // Search matches the localized card title (host name included verbatim).
  const visible = tables.filter(tb =>
    query.trim() === '' || tableTitle(tb).toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    // flex column fills the available viewport height so Footer's
    // marginTop:'auto' sticks flush to the bottom on short pages (see
    // AppShell.tsx's content-wrapper comment).
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      {/* ── QUICK MATCH banner ──────────────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: isMobile ? 20 : 35, paddingBottom: isMobile ? 12 : 19 }}>
        <AtmosphericGlow width={510} height={326} top={0} leftOffset={254} radius={337} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <div
            style={{
              position:     'relative',
              overflow:     'hidden',
              display:      'flex',
              flexWrap:     'wrap',
              alignItems:   'center',
              justifyContent:'space-between',
              gap:          isMobile ? 16 : 24,
              padding:      isMobile ? '20px 18px' : '27px 32px',
              borderRadius: isMobile ? 13 : 14.3,
              border:       `1px solid ${DS2.color.gold}`,
              background:   '#000000',
            }}
          >
            {/* Domino render (Figma Tile-lobby-cta) — the source asset is shot on
                a grey backdrop (no alpha channel), so a radial mask fades the
                whole image to transparent at its edges instead of showing a hard
                grey rectangle; the domino itself stays crisp near the center. */}
            <img
              src={ASSETS.marketing.LOBBY_BANNER}
              alt=""
              aria-hidden="true"
              style={{
                position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)',
                height: '145%', width: 'auto', objectFit: 'contain',
                WebkitMaskImage: 'radial-gradient(ellipse 60% 65% at 58% 50%, black 42%, transparent 90%)',
                maskImage:       'radial-gradient(ellipse 60% 65% at 58% 50%, black 42%, transparent 90%)',
                pointerEvents: 'none', zIndex: 0,
              }}
              draggable={false}
            />
            <div style={{ position: 'relative', zIndex: 1, flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 6 : 9.3 }}>
              <div style={{ ...EYEBROW, ...(isMobile && { fontSize: 13 }) }}>{t('online.quickEyebrow')}</div>
              <h1 style={{ ...SECTION_HEADING, color: '#F5F0E0', ...(isMobile && { fontSize: 22, lineHeight: '26px' }) }}>
                {t('online.quickTitle')} <GradientText>{t('online.quickTitleAccent')}</GradientText>
              </h1>
              {!isMobile && (
                <p style={{ ...PARAGRAPH, maxWidth: 647 }}>
                  {t('online.quickBody')}
                </p>
              )}
            </div>
            <div style={{ position: 'relative', zIndex: 1, width: isMobile ? '100%' : undefined }}>
              <GoldActionButton onClick={handleFindTable} busy={busy} compact={isMobile}>{t('online.findTable')}</GoldActionButton>
            </div>
          </div>
        </PageContent>
      </div>

      {/* ── stats row ───────────────────────────────────────────────── */}
      <PageContent maxWidth={DS2.layout.contentMaxWidth}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 20 : 29.3, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? 8 : 9.3 }}>
            <StatusDot color="#00FF09" size={isMobile ? 9 : 11} />
            <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: isMobile ? 18 : 24, color: DS2.color.gold, letterSpacing: '0.04em' }}>{playingNow}</span>
            <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: isMobile ? 13 : 17.3, color: DS2.color.textBright, letterSpacing: '0.05em' }}>{t('online.playingNow')}</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? 8 : 9.3 }}>
            <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: isMobile ? 18 : 24, color: DS2.color.gold, letterSpacing: '0.04em' }}>{openTables}</span>
            <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: isMobile ? 13 : 17.3, color: DS2.color.textBright, letterSpacing: '0.05em' }}>{t('online.openTables', { count: openTables })}</span>
          </span>
        </div>
      </PageContent>

      {/* ── Play online header + search ─────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: isMobile ? 20 : 29.3 }}>
        {/* Big left ambient glow (Figma 1459×1530 ×0.75, offset toward the
            left edge behind the content). Sized down from the raw Figma
            value (1147 tall) — that height extended past this section's
            real content when the live grid has only 1-2 tables, bleeding
            past Footer and inflating the page's scrollable height. */}
        <AtmosphericGlow width={1094} height={700} top={0} leftOffset={-737} radius={627} opacity={0.18} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'flex-end', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: isMobile ? 12 : 18.7, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 4 : 7.3 }}>
              <h2 style={{ ...SECTION_HEADING, ...(isMobile && { fontSize: 20, lineHeight: '24px' }) }}>{t('online.heading')}</h2>
              {!isMobile && <p style={PARAGRAPH}>{t('online.sub')}</p>}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 10, background: '#FFFFFF', border: '1.5px solid #C9A84C', borderRadius: isMobile ? 12 : 14, padding: isMobile ? '10px 16px' : '11px 18px', minWidth: isMobile ? 0 : 260, flex: isMobile ? undefined : '0 1 373px' }}>
              <SearchIcon size={isMobile ? 17 : 18} />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('online.searchPlaceholder')}
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: '#0B0B0B', fontFamily: DS2.font.body, fontSize: isMobile ? 14 : 16 }}
              />
            </label>
          </div>

          {error && (
            <p style={{ marginTop: 16, color: DT.color.error, fontFamily: DS2.font.body, fontSize: 15 }}>{renderUiMessage(t, error)}</p>
          )}

          {/* ── table grid — live sessions from apiClient.getSessions() ── */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(313px, 1fr))', gap: isMobile ? 12 : 19.3, marginTop: isMobile ? 20 : 28, paddingBottom: isMobile ? 32 : 51 }}>
            <CreateTableCard onClick={handleCreate} busy={busy} compact={isMobile} />
            {visible.map(tb => <TableCard key={tb.sessionId} table={tb} onJoin={handleJoinTable} compact={isMobile} />)}
          </div>
          {loadingSessions && <Spinner />}
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}

function SearchIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="#0B0B0B" strokeWidth="2" />
      <path d="M20 20l-3.2-3.2" stroke="#0B0B0B" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
